import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CardAttachmentListModel,
  CardAttachmentModel,
  CardAttachmentUploadResultModel,
} from '../models/card-details-api.models';
import { asArray, asNullableString, asNumber, asRecord, asString } from './card-api.utils';

@Injectable({ providedIn: 'root' })
export class CardAttachmentsApiService {
  private readonly http = inject(HttpClient);

  async getByCard(cardId: string): Promise<CardAttachmentListModel> {
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(`/api/cards/${cardId}/attachments`)
      );
      const items = asArray(response)
        .map((entry) => this.mapAttachment(entry, cardId))
        .filter((entry) => !!entry.id);
      return { items };
    } catch (error) {
      if (this.isNoAttachmentsError(error)) {
        return { items: [] };
      }
      throw error;
    }
  }

  async upload(cardId: string, file: File): Promise<CardAttachmentUploadResultModel> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await firstValueFrom(
      this.http.post<unknown>(`/api/cards/${cardId}/attachments`, formData)
    );
    return this.mapUploadResponse(response, file.name, cardId);
  }

  async delete(cardId: string, attachmentId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/cards/${cardId}/attachments/${attachmentId}`));
  }

  private mapAttachment(raw: unknown, cardId: string): CardAttachmentModel {
    const source = asRecord(raw);
    const read = (key: string): string | null => asNullableString(source[key]);
    const url =
      read('url') ??
      read('downloadUrl') ??
      read('publicUrl') ??
      read('assetUrl') ??
      read('fileUrl') ??
      read('path') ??
      read('storagePath');
    return {
      id: asString(source['id']),
      cardId: asString(source['cardId']) || cardId,
      uploadedByUserId: asString(source['uploadedByUserId']),
      fileName: asString(source['fileName']),
      originalFileName: asString(source['originalFileName']) || asString(source['fileName']),
      contentType: asString(source['contentType']),
      extension: asString(source['extension']),
      fileSize: asNumber(source['fileSize'], 0),
      uploadedAt: asString(source['uploadedAt']) || asString(source['createdAt']),
      url,
      assetUrl: read('assetUrl'),
      fileUrl: read('fileUrl'),
      path: read('path') ?? read('storagePath'),
    };
  }

  private mapUploadResponse(
    raw: unknown,
    fallbackFileName: string,
    cardId: string
  ): CardAttachmentUploadResultModel {
    const source = asRecord(raw);
    const data = asRecord(source['data']);
    const attachment = asRecord(source['attachment']);
    const nested = Object.keys(data).length > 0 ? data : attachment;
    const read = (key: string): string | null =>
      asNullableString(source[key]) ?? asNullableString(nested[key]);

    const id = read('id');
    const url =
      read('url') ??
      read('downloadUrl') ??
      read('publicUrl') ??
      read('assetUrl') ??
      read('fileUrl') ??
      read('path') ??
      read('storagePath');

    return {
      id,
      fileName: read('fileName') ?? fallbackFileName,
      originalFileName: read('originalFileName') ?? fallbackFileName,
      storageKey: read('storageKey') ?? read('key'),
      url,
      assetUrl: read('assetUrl'),
      fileUrl: read('fileUrl'),
      path: read('path') ?? read('storagePath'),
    };
  }

  private isNoAttachmentsError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
      return false;
    }
    const payload = asRecord(error.error);
    const message = asString(payload['message']).toLowerCase();
    if (!message) return false;
    return (
      message.includes('no attached files available') ||
      message.includes('nao ha anexos') ||
      message.includes('não há anexos')
    );
  }
}
